import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { trackError } from "@/utils/error-tracking";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider } from "@/components/AuthProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { ClassListPage } from "@/pages/ClassListPage";
import { JournalPage } from "@/pages/JournalPage";
import { MovementCatalogPage } from "@/pages/MovementCatalogPage";
import { SettlementPage } from "@/pages/SettlementPage";
import { PendingJournalsPage } from "@/pages/PendingJournalsPage";
import { CompletedJournalsPage } from "@/pages/CompletedJournalsPage";
import { MembersPage } from "@/pages/MembersPage";
import { MemberDetailPage } from "@/pages/MemberDetailPage";
import { TicketCatalogPage } from "@/pages/TicketCatalogPage";
import { ToastProvider } from "@/components/ToastProvider";
import { isDemoMode } from "@/demo/demoMode";
import { usePageTracking } from "@/hooks/usePageTracking";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        trackError(error, { source: "mutation" });
      },
    },
  },
});

function AppRoutes() {
  usePageTracking({ enabled: !isDemoMode() });
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes"
        element={
          <ProtectedRoute>
            <ClassListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pending-journals"
        element={
          <ProtectedRoute>
            <PendingJournalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/completed-journals"
        element={
          <ProtectedRoute>
            <CompletedJournalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:classId/journal"
        element={
          <ProtectedRoute>
            <JournalPage />
          </ProtectedRoute>
        }
      />
      {/* 구버전 호환: memberId 포함 URL → JournalPage로 리다이렉트 (memberId 무시) */}
      <Route
        path="/classes/:classId/journal/:memberId"
        element={
          <ProtectedRoute>
            <JournalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/members"
        element={
          <ProtectedRoute>
            <MembersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/members/:memberId"
        element={
          <ProtectedRoute>
            <MemberDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ticket-catalog"
        element={
          <ProtectedRoute>
            <TicketCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/movements"
        element={
          <ProtectedRoute>
            <MovementCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settlement"
        element={
          <ProtectedRoute>
            <SettlementPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ToastProvider>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </ToastProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
